import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Table,
  Typography,
  Spin,
  Button,
  Space,
  message,
} from 'antd';
import {
  DeleteOutlined,
  FilePptOutlined,
  FilePdfOutlined,
  PictureOutlined,
  FileOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { mekakApi, formatDateShort } from '../services';
import type { FileDocumentDetail, KnowledgeImage } from '../models';
import ImageGallery from './ImageGallery';

const { Text } = Typography;

const imageTypes = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

const fileIcon = (type: string) => {
  if (type === 'pptx') return <FilePptOutlined style={{ fontSize: 24, color: '#d4380d' }} />;
  if (type === 'pdf') return <FilePdfOutlined style={{ fontSize: 24, color: '#cf1322' }} />;
  if (imageTypes.includes(type)) return <PictureOutlined style={{ fontSize: 24, color: '#1677ff' }} />;
  return <FileOutlined style={{ fontSize: 24 }} />;
};

interface FileDocumentDetailModalProps {
  open: boolean;
  fileDocId: string | null;
  onClose: () => void;
  onDeleted?: () => void;
  onKnowledgeClick?: (knowledgeId: string) => void;
}

const FileDocumentDetailModal: React.FC<FileDocumentDetailModalProps> = ({
  open,
  fileDocId,
  onClose,
  onDeleted,
  onKnowledgeClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<FileDocumentDetail | null>(null);

  const fetchData = useCallback(async () => {
    if (!fileDocId) return;
    setLoading(true);
    try {
      const res = await mekakApi.getFileDocumentDetail(fileDocId);
      if (res.data.success) {
        setDetail(res.data.data);
      }
    } catch {
      message.error('載入檔案詳情失敗');
    } finally {
      setLoading(false);
    }
  }, [fileDocId]);

  useEffect(() => {
    if (open && fileDocId) {
      setDetail(null);
      fetchData();
    }
  }, [open, fileDocId, fetchData]);

  const handleDelete = () => {
    if (!fileDocId || !detail) return;
    Modal.confirm({
      title: '確認刪除',
      content: `刪除「${detail.file_document.original_filename}」及其所有知識點？`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await mekakApi.deleteFileDocument(fileDocId);
        message.success('檔案及相關知識點已刪除');
        onClose();
        onDeleted?.();
      },
    });
  };

  const doc = detail?.file_document;
  const knowledge = detail?.knowledge ?? [];
  const images = detail?.images ?? [];

  const knowledgeColumns: ColumnsType<(typeof knowledge)[0]> = [
    {
      title: '標題',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 500, textAlign: 'left', whiteSpace: 'normal', height: 'auto' }}
          onClick={() => onKnowledgeClick?.(record.id)}
        >
          {title}
        </Button>
      ),
    },
    {
      title: '頁碼',
      dataIndex: 'page_numbers',
      key: 'pages',
      width: 120,
      render: (pages: number[]) =>
        pages?.length ? (
          <Space size={2} wrap>
            {pages.map(p => (
              <Tag key={p} color="blue" style={{ margin: 0 }}>p.{p}</Tag>
            ))}
          </Space>
        ) : '-',
    },
    {
      title: '建立日期',
      dataIndex: 'created_at',
      key: 'date',
      width: 100,
      render: (d: string) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDateShort(d)}</Text>,
    },
  ];

  return (
    <Modal
      title={
        doc ? (
          <Space>
            {fileIcon(doc.file_type)}
            <span>{doc.original_filename}</span>
            <Tag color={doc.processing_status === 'completed' ? 'green' : 'orange'}>
              {doc.processing_status === 'completed' ? '已完成' : doc.processing_status}
            </Tag>
          </Space>
        ) : '檔案詳情'
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        doc
          ? [
              <Button key="delete" danger icon={<DeleteOutlined />} onClick={handleDelete}>
                刪除檔案
              </Button>,
              <Button key="close" type="primary" onClick={onClose}>
                關閉
              </Button>,
            ]
          : null
      }
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : doc ? (
        <div style={{ maxHeight: 'calc(80vh - 120px)', overflow: 'auto' }}>
          {/* File metadata */}
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="檔案類型">{doc.file_type.toUpperCase()}</Descriptions.Item>
            <Descriptions.Item label="檔案大小">{(doc.file_size_bytes / 1024).toFixed(1)} KB</Descriptions.Item>
            <Descriptions.Item label="頁數">{doc.page_count}</Descriptions.Item>
            <Descriptions.Item label="知識點數">{doc.knowledge_count}</Descriptions.Item>
            <Descriptions.Item label="成本">{doc.total_cost ? `$${doc.total_cost.toFixed(6)}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="上傳日期">{formatDateShort(doc.created_at)}</Descriptions.Item>
          </Descriptions>

          {/* Images */}
          {images.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                圖片 ({images.length})
              </Text>
              <ImageGallery images={images} editable onImageUpdated={fetchData} />
            </div>
          )}

          {/* Knowledge entries */}
          {knowledge.length > 0 && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                知識點 ({knowledge.length})
              </Text>
              <Table
                rowKey="id"
                columns={knowledgeColumns}
                dataSource={knowledge}
                size="small"
                pagination={false}
              />
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text type="secondary">找不到此檔案</Text>
        </div>
      )}
    </Modal>
  );
};

export default FileDocumentDetailModal;
